import { isAsyncPace } from '../../shared/config';
import { isSoloMode } from '../../shared/modes';
import type { RoomSessionView } from '../session/room-session';

export type Page = 'menu' | 'solo' | 'versus' | 'host' | 'join' | 'leaderboard' | 'competitive';
export type Navigation =
  | { kind: 'page'; page: Page; parent: Navigation | null }
  | { kind: 'room'; parent: Navigation; roomCode?: string; lobbyRoundId?: string }
  | { kind: 'settings'; parent: Navigation }
  | { kind: 'archive'; roundId: string; parent: Navigation };
export type NavigationEvent =
  | { type: 'open'; page: Page | 'settings' }
  | { type: 'room' }
  | { type: 'results-lobby'; roundId: string }
  | { type: 'view-results' }
  | { type: 'archive'; roundId: string }
  | { type: 'join-required' }
  | { type: 'back' }
  | { type: 'home' };
export const home = (): Navigation => ({ kind: 'page', page: 'menu', parent: null });

export function versusForm(page: 'host' | 'join'): Navigation {
  const versus: Navigation = { kind: 'page', page: 'versus', parent: home() };
  return { kind: 'page', page, parent: versus };
}

export function initialNavigation(route: { roomCode: string | null; resultId: string | null }): Navigation {
  if (route.resultId) return { kind: 'archive', roundId: route.resultId, parent: home() };
  return route.roomCode ? versusForm('join') : home();
}

export function transition(state: Navigation, event: NavigationEvent): Navigation {
  switch (event.type) {
    case 'archive':
      return { kind: 'archive', roundId: event.roundId, parent: state };
    case 'home':
      return home();
    case 'back':
      return state.parent ?? home();
    case 'join-required':
      return versusForm('join');
    case 'results-lobby':
      return state.kind === 'room' ? { ...state, lobbyRoundId: event.roundId } : state;
    case 'view-results':
      // Return from a locally dismissed recap to the completed round's results.
      if (state.kind !== 'room' || !state.lobbyRoundId) return state;
      const { lobbyRoundId: _dismissed, ...rest } = state;
      return rest;
    case 'room':
      return state.kind === 'room' ? state : { kind: 'room', parent: state };
    case 'open':
      return event.page === 'settings'
        ? { kind: 'settings', parent: state }
        : { kind: 'page', page: event.page, parent: state };
  }
}

/** Settings overlays its parent; the server session still chooses the parent's current room screen. */
export function visibleNavigation(state: Navigation): Navigation {
  return state.kind === 'settings' ? state.parent : state;
}

export function explicitArchiveNavigation(
  state: Navigation,
): Extract<Navigation, { kind: 'archive' }> | null {
  const visible = visibleNavigation(state);
  return visible.kind === 'archive' ? visible : null;
}

export function identifyRoomNavigation(state: Navigation, roomCode: string): Navigation {
  if (state.kind === 'room') return { ...state, roomCode };
  if (state.kind === 'settings') return { ...state, parent: identifyRoomNavigation(state.parent, roomCode) };
  if (state.kind === 'archive' && hasRoomContext(state.parent))
    return { ...state, parent: identifyRoomNavigation(state.parent, roomCode) };
  return state;
}

export function hasRoomContext(state: Navigation): boolean {
  let current: Navigation | null = state;
  while (current) {
    if (current.kind === 'room') return true;
    current = current.parent;
  }
  return false;
}

/** A history entry cannot re-enter a room after its session has been left or detached. */
export function restorableNavigation(state: Navigation, activeRoomCode: string | null): Navigation {
  if (state.kind === 'archive') {
    const parent = restorableNavigation(state.parent, activeRoomCode);
    return parent === state.parent ? state : { ...state, parent };
  }
  if (state.kind === 'settings') {
    const parent = restorableNavigation(state.parent, activeRoomCode);
    return parent === state.parent ? state : parent;
  }
  if (state.kind !== 'room' || (activeRoomCode !== null && (state.roomCode ?? '') === activeRoomCode))
    return state;
  return state.parent.kind === 'page' ? state.parent : home();
}

export type Screen =
  | Page
  | 'settings'
  | 'loading'
  | 'setup'
  | 'lobby'
  | 'game'
  | 'results'
  | 'archive'
  | 'waiting'
  | 'turn-lobby'
  | 'match-lobby';

export function destination(
  state: Navigation,
  session: Pick<RoomSessionView, 'hasSnapshot' | 'inSetupLobby' | 'phase'> &
    Partial<
      Pick<
        RoomSessionView,
        | 'archivedResult'
        | 'finished'
        | 'results'
        | 'begun'
        | 'spectator'
        | 'activeConfig'
        | 'config'
        | 'dismissedResultsRoundId'
        | 'mode'
      >
    >,
): Screen {
  if (state.kind === 'archive') return 'archive';
  if (state.kind === 'page') return state.page;
  if (state.kind === 'settings') return 'settings';
  if (session.archivedResult) return 'archive';
  // Socket readiness gates interaction, not the last authoritative room view.
  if (!session.hasSnapshot) return 'loading';
  if (session.phase === 'lobby')
    // A ranked match lobby only waits for both players; it has no roster controls.
    return session.inSetupLobby ? 'setup' : session.mode === 'competitive' ? 'match-lobby' : 'lobby';
  if (session.phase === 'results' && state.lobbyRoundId && state.lobbyRoundId === session.results?.roundId)
    // A dismissed Turns recap returns to the turn lobby, never the Live room lobby.
    return isAsyncPace(session.activeConfig ?? session.results?.config ?? session.config)
      ? 'turn-lobby'
      : 'lobby';
  if (session.phase === 'playing' && isAsyncPace(session.activeConfig ?? session.config)) {
    // A turn-taking round: play only during your own turn; otherwise the turn lobby, with
    // the last round's results shown first until dismissed and recallable afterwards.
    if (session.begun && !session.finished && !session.spectator) return 'game';
    if (session.results && session.dismissedResultsRoundId !== session.results.roundId) return 'results';
    return 'turn-lobby';
  }
  if (session.finished && session.phase !== 'results') return 'waiting';
  return session.phase === 'results' ? 'results' : 'game';
}

export function backDestination(
  state: Navigation,
  session: Pick<RoomSessionView, 'inSetupLobby' | 'joined'> & Partial<Pick<RoomSessionView, 'mode'>>,
): Navigation {
  if (state.kind === 'room' && session.mode === 'versus') return roomFormDestination(state);
  if (state.kind === 'room' && session.mode === 'competitive')
    return transition(home(), { type: 'open', page: 'competitive' });
  if (state.kind === 'room' && session.inSetupLobby && session.mode) {
    // A restored room may have Join as its transient parent, not its mode's menu.
    return isSoloMode(session.mode) ? transition(home(), { type: 'open', page: 'solo' }) : home();
  }
  return state.kind === 'room' && session.joined && !session.inSetupLobby ? home() : (state.parent ?? home());
}

function roomFormDestination(room: Extract<Navigation, { kind: 'room' }>): Navigation {
  const parent = room.parent;
  return parent.kind === 'page' && (parent.page === 'host' || parent.page === 'join')
    ? parent
    : versusForm('join');
}
